// tslint:disable:max-classes-per-file no-any

import { CRAWLER_REGEX_PATTERNS } from './constants';

export type MixpanelCallback = () => void;

interface MixpanelLib {
  get_distinct_id(): string;
  alias(alias: string, original?: string): void;
  track(eventName: string, properties?: { [key: string]: any }, eventCallback?: MixpanelCallback): void;
  register(properties: { [key: string]: any }): void;
}

const DEFAULT_MSG_NOT_INITIALIZED = 'Mixpanel is either not initialized or is still loading.';
class MixpanelNotInitializedError extends Error {
  constructor(message?: string) {
    super(message || DEFAULT_MSG_NOT_INITIALIZED);
    this.name = 'MixpanelNotInitializedError';
  }
}

// ****************************************************************************
// Common MixPanel Events
//
export const MIXPANEL_EVENT_PAGE_VIEW = 'Page view';
export const MIXPANEL_EVENT_CLICK = 'Click';

// ****************************************************************************
// Common MixPanel Properties
//
export const MIXPANEL_PROPERTY_PAGE_TITLE = 'Title';
export const MIXPANEL_PROPERTY_FIRST_NAME = '$first_name';
export const MIXPANEL_PROPERTY_LAST_NAME = '$last_name';
export const MIXPANEL_PROPERTY_ACCOUNT_CREATION_DATE = '$created'; // ISO8601 date-time string (YYYY-MM-DDThh:mm:ss)
export const MIXPANEL_PROPERTY_EMAIL_ADDRESS = '$email';
export const MIXPANEL_PROPERTY_PHONE_NUMBER = '$phone';

// ****************************************************************************
// Distinct ID returned in Dry Run mode
//
const MIXPANEL_DRY_RUN_DISTINCT_ID = '00000000000000-00000000000000-0000000-000000-00000000000000';

export class MixpanelService {
  public static UninitializedBehavior: 'ignore' | 'ignoreWithWarning' | 'reject' = 'ignoreWithWarning';
  public static DebugMode: boolean = false;
  public static DryRun: boolean = false;

  private static _loading: boolean = false;
  private static _instance: MixpanelLib;

  public static get isInitialized(): boolean {
    return MixpanelService.DryRun || (typeof MixpanelService._instance === 'object' && MixpanelService._instance !== null);
  }

  public static initialize(token: string): Promise<void> {
    // already initialized?
    if (MixpanelService._instance) {
      MixpanelService.log('INFO: MixpanelService::initialize: Call ignored. (Mixpanel is already initialized.)');
      return Promise.resolve();
    }

    const mixpanelToken = typeof token === 'string' && token.trim();
    // @ts-ignore Property 'mixpanel' does not exist on type 'Window'
    if (typeof window.mixpanel === 'object' && window.mixpanel !== null && mixpanelToken) {
      if (!MixpanelService._loading) {
        if (MixpanelService.DryRun) {
          MixpanelService.log('DEBUG: MixpanelService::initialize: Call ignored. (DryRun flag is set.)');
          return Promise.resolve();
        }

        MixpanelService._loading = true;
        return new Promise<void>((resolve, reject) => {
          try {
            // @ts-ignore Property 'mixpanel' does not exist on type 'Window'
            window.mixpanel.init(token, {
              autotrack: false,
              track_pageview: false,
              debug: MixpanelService.DebugMode,
              loaded(instance: MixpanelLib) {
                MixpanelService._instance = instance;
                MixpanelService._loading = false;

                // ------------------------------------------------------------
                // ignore any traffic from search crawlers and site monitoring
                // agents
                const { userAgent } = navigator;
                instance.register({ 'User Agent': userAgent });
                const re = new RegExp(`(${CRAWLER_REGEX_PATTERNS.join('|')})`, 'i');
                if (re.test(userAgent)) {
                  instance.register({ '$ignore': true });
                }
                // ------------------------------------------------------------

                resolve();
                return;
              }
            });
          } catch (error) {
            reject(error);
          }
          return;
        });
      }
    } else {
      MixpanelService.log(
        `WARNING: MixpanelService::initialize: Call ignored. (${
          mixpanelToken ? 'Global mixpanel object was not found.' : 'Invalid token.'
        })`
      );
    }

    return Promise.resolve();
  }

  public static getDistinctId(): Promise<string> {
    let distinctId: string | undefined;
    try {
      if (MixpanelService.DryRun) {
        MixpanelService.log('DEBUG: MixpanelService::getDistinctId:', MIXPANEL_DRY_RUN_DISTINCT_ID);
        distinctId = MIXPANEL_DRY_RUN_DISTINCT_ID;
      } else if (MixpanelService._instance) {
        distinctId = MixpanelService._instance.get_distinct_id();
      } else {
        if (MixpanelService.UninitializedBehavior === 'reject') {
          throw new MixpanelNotInitializedError();
        } else if (MixpanelService.UninitializedBehavior === 'ignoreWithWarning') {
          MixpanelService.log('WARNING: MixpanelService::track:', DEFAULT_MSG_NOT_INITIALIZED);
          distinctId = '';
        }
      }

      if (typeof distinctId === 'string') {
        return Promise.resolve(distinctId);
      }

      return Promise.reject();
    } catch (e) {
      return Promise.reject(e);
    }
  }

  public static alias(alias: string, original?: string): Promise<void> {
    if (MixpanelService.DryRun) {
      MixpanelService.log('DEBUG: MixpanelService::alias:', alias);
    } else if (MixpanelService._instance) {
      try {
        MixpanelService._instance.alias(alias, original);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    } else {
      if (MixpanelService.UninitializedBehavior === 'reject') {
        return Promise.reject(new MixpanelNotInitializedError());
      } else if (MixpanelService.UninitializedBehavior === 'ignoreWithWarning') {
        MixpanelService.log('WARNING: MixpanelService::alias:', DEFAULT_MSG_NOT_INITIALIZED);
      }
    }
    return Promise.resolve();
  }

  public static track(eventName: string, properties?: { [key: string]: any }): Promise<void> {
    if (MixpanelService.DryRun) {
      MixpanelService.log('DEBUG: MixpanelService::track:', { event: eventName, properties });
    } else if (MixpanelService._instance) {
      return new Promise((resolve, reject) => {
        try {
          MixpanelService._instance.track(eventName, properties, resolve);
        } catch (e) {
          reject(e);
        }
      });
    } else {
      if (MixpanelService.UninitializedBehavior === 'reject') {
        return Promise.reject(new MixpanelNotInitializedError());
      } else if (MixpanelService.UninitializedBehavior === 'ignoreWithWarning') {
        MixpanelService.log('WARNING: MixpanelService::track:', DEFAULT_MSG_NOT_INITIALIZED);
      }
    }
    return Promise.resolve();
  }

  public static trackPageView(pageTitle?: string, properties?: { [key: string]: any }): Promise<void> {
    const titleProp = typeof pageTitle === 'string' && { [MIXPANEL_PROPERTY_PAGE_TITLE]: pageTitle };
    const props = Object.assign({}, titleProp, properties);
    return MixpanelService.track(MIXPANEL_EVENT_PAGE_VIEW, props);
  }

  public static trackElementClick(
    elementSelectors: string | string[],
    once: boolean = false,
    propsCallback?: (event: MouseEvent) => { [key: string]: any } | null | undefined | void
  ) {
    const elementList = (([] as string[]).concat.apply(([] as string[]), [elementSelectors]))
      .map((selector) => document.querySelector<HTMLElement>(selector));

    elementList.forEach((element: HTMLElement | null) => {
      if (element === null) {
        return;
      }
      element.addEventListener(
        'click',
        (event: MouseEvent) => {
          let mpEventName = '';
          let props: { [key: string]: any } | undefined;
          if (typeof propsCallback === 'function') {
            const elementProps = propsCallback(event);
            if (typeof elementProps === 'object' && elementProps !== null) {
              const { $mpEventName, ...otherProps } = elementProps;
              if (typeof $mpEventName === 'string') {
                mpEventName = $mpEventName.trim();
              }
              props = otherProps;
            }
          }
          MixpanelService.track(mpEventName || MIXPANEL_EVENT_CLICK, props);
        },
        { once }
      );
    });
  }

  private static log(message?: any, ...optionalParams: any[]): void {
    if (MixpanelService.DebugMode || MixpanelService.DryRun) {
      // tslint:disable-next-line:no-console
      console.log(message, ...optionalParams);
    }
  }
}
// tslint:enable:max-classes-per-file no-any
